import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold ring-offset-background transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-neon active:bg-primary/80",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80",
        outline:
          "border border-border bg-transparent hover:bg-secondary hover:text-secondary-foreground active:bg-secondary/80",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/60",
        ghost:
          "hover:bg-secondary hover:text-secondary-foreground active:bg-secondary/80",
        link:
          "text-primary underline-offset-4 hover:underline active:opacity-70",
        // Premium variants
        hero:
          "bg-primary text-primary-foreground font-bold shadow-neon hover:shadow-neon-strong hover:scale-105 active:scale-[0.98]",
        heroOutline:
          "border-2 border-primary text-primary bg-transparent hover:bg-primary/10 hover:shadow-neon active:bg-primary/20",
        nav:
          "text-muted-foreground hover:text-foreground bg-transparent active:text-foreground",
        // Glass variants
        glass:
          "bg-card/80 backdrop-blur-xl border border-border text-foreground hover:bg-card/90 hover:border-primary/30",
        "glass-primary":
          "bg-primary/10 backdrop-blur-xl border border-primary/30 text-primary hover:bg-primary/20 hover:shadow-neon",
        // Trading variants
        buy:
          "bg-success text-white font-bold hover:bg-success/90 hover:shadow-[0_0_20px_hsl(142_76%_46%/0.3)]",
        sell:
          "bg-destructive text-white font-bold hover:bg-destructive/90 hover:shadow-[0_0_20px_hsl(0_84%_60%/0.3)]",
        // Subtle variants
        success:
          "bg-success/15 text-success hover:bg-success/25",
        warning:
          "bg-warning/15 text-warning hover:bg-warning/25",
      },
      size: {
        default: "h-9 px-4 py-2",           // 36px - Tier 1 primary CTAs
        sm: "h-7 px-3 py-1 text-sm",        // 28px - Tier 2 standard
        lg: "h-12 px-8 text-base",          // 48px - Hero CTAs only
        icon: "h-7 w-7",                    // 28px - Standard icon
        "icon-lg": "h-9 w-9",               // 36px - Large icon (primary)
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Show loading spinner */
  loading?: boolean;
  /** Icon to display before children */
  leftIcon?: React.ReactNode;
  /** Icon to display after children */
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    // When loading, show spinner and disable
    const isDisabled = disabled || loading;

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {children}
          </>
        ) : (
          <>
            {leftIcon}
            {children}
            {rightIcon}
          </>
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

// Quick amount buttons for trading
interface QuickAmountButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  amount: number;
  currency?: string;
  selected?: boolean;
}

const QuickAmountButton = React.forwardRef<
  HTMLButtonElement,
  QuickAmountButtonProps
>(({ amount, currency = "SOL", selected, className, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "flex-1 h-7 px-3 rounded-lg text-sm font-medium transition-all duration-150",
        "border border-border bg-secondary/50 hover:bg-secondary hover:border-primary/30",
        selected && "border-primary bg-primary/10 text-primary",
        className
      )}
      {...props}
    >
      {amount} {currency}
    </button>
  );
});
QuickAmountButton.displayName = "QuickAmountButton";

export { Button, buttonVariants, QuickAmountButton };
